
import React, { useState } from 'react';
import SectionSelector from './SectionSelector';
import FormInput from './components/FormInput';
import FormTextarea from './components/FormTextarea';
import AddButton from './components/AddButton';
import RemoveButton from './components/RemoveButton';
import ArrayFieldCard from './components/ArrayFieldCard';
import WebsiteLinksInput from './components/WebsiteLinksInput';
import CategorizedSkillsInput from './components/CategorizedSkillsInput';
import useArrayField from './hooks/useArrayField';

const SimpleResumeForm = ({ resumeData, onUpdate, validationErrors = {}, onFieldFocus, onFieldBlur, getDynamicSections, renderDynamicSectionForm }) => {
  const [activeSection, setActiveSection] = useState('personal');
  const [newWebsite, setNewWebsite] = useState("");
  
  const updateField = (field, data) => {
    onUpdate({ ...resumeData, [field]: data });
  };

  const handlePersonalChange = (field, value) => {
    updateField(field, value);
  };

  const addHighlight = () => {
    updateField("highlights", [...(resumeData.highlights || []), ""]);
  };

  const updateHighlight = (index, value) => {
    const updated = [...(resumeData.highlights || [])];
    updated[index] = value;
    updateField("highlights", updated);
  };

  const removeHighlight = (index) => {
    updateField("highlights", resumeData.highlights.filter((_, i) => i !== index));
  };

  // Use the useArrayField hook for experience
  const experienceField = useArrayField(
    resumeData.work_experience || [],
    (newItems) => updateField("work_experience", newItems),
    { position: "", company: "", location: "", start_date: "", end_date: "", duties: [] }
  );

  const addDuty = (expIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].duties.push("");
    updateField("work_experience", updated);
  };

  const updateDuty = (expIndex, dutyIndex, value) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].duties[dutyIndex] = value;
    updateField("work_experience", updated);
  };

  const removeDuty = (expIndex, dutyIndex) => {
    const updated = [...(resumeData.work_experience || [])];
    updated[expIndex].duties.splice(dutyIndex, 1);
    updateField("work_experience", updated);
  };

  // removeExperience is now available via experienceField.removeItem

  const addEducation = () => {
    updateField("education", [...(resumeData.education || []), {
      degree: "", institute: "", location: "", start_year: "", end_year: "", cgpa: ""
    }]);
  };

  const updateEducation = (index, field, value) => {
    const updated = [...(resumeData.education || [])];
    updated[index] = { ...updated[index], [field]: value };
    updateField("education", updated);
  };

  const removeEducation = (index) => {
    updateField("education", resumeData.education.filter((_, i) => i !== index));
  };

  const addWebsite = () => {
    if (newWebsite.trim()) {
      updateField("websites", [...(resumeData.websites || []), newWebsite.trim()]);
      setNewWebsite("");
    }
  };

  const removeWebsite = (index) => {
    updateField("websites", resumeData.websites.filter((_, i) => i !== index));
  };

  const addProject = () => {
    updateField("projects", [...(resumeData.projects || []), {
        title: "",
        name: "", // Keep for compatibility
        description: "",
        github_link: "",
        link: "", // Add link for compatibility
        tech_stack: [],
        technologies: [] // Add technologies for compatibility
    }]);
  };

  const removeProject = (index) => {
    updateField("projects", resumeData.projects.filter((_, i) => i !== index));
  };

  const updateProject = (index, field, value) => {
    const updated = [...(resumeData.projects || [])];
    updated[index] = { ...updated[index], [field]: value };
    
    // Maintain data consistency across templates
    if (field === 'tech_stack') {
      updated[index].technologies = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'technologies') {
      updated[index].tech_stack = Array.isArray(value) ? value : value.split(',').map(t => t.trim());
    }
    if (field === 'github_link') {
      updated[index].link = value;
    }
    if (field === 'link') {
      updated[index].github_link = value;
    }
    
    updateField("projects", updated);
  };

  const baseSections = [
    { id: 'personal', label: 'Personal Info' },
    { id: 'summary', label: 'Summary' },
    { id: 'highlights', label: 'Highlights' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'skills', label: 'Skills' },
    { id: 'achievements', label: 'Achievements' },
    { id: 'certifications', label: 'Certifications' },
    { id: 'projects', label: 'Projects' },
    { id: 'additional', label: 'Additional' }
  ];

  // Combine base sections with dynamic sections from ResumeEditor
  const sections = getDynamicSections ? [...baseSections, ...getDynamicSections()] : baseSections;

  const renderSection = () => {
    switch (activeSection) {
      case 'personal':
        return (
          <div className="form-group">
            <h3>Personal Information</h3>
            <FormInput
              label="Full Name"
              name="full_name"
              value={resumeData?.full_name || ""}
              onChange={(value) => handlePersonalChange("full_name", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="YOUR NAME"
              error={validationErrors.full_name}
            />
            <FormTextarea
              label="Address"
              name="address"
              value={resumeData?.address || ""}
              onChange={(value) => handlePersonalChange("address", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="Your Address..."
              rows={3}
            />
            <FormInput
              label="Phone Number"
              name="phone"
              value={resumeData?.phone || ""}
              onChange={(value) => handlePersonalChange("phone", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="+1 (555) 123-4567"
              error={validationErrors.phone}
            />
            <FormInput
              label="Email Address"
              name="email"
              type="email"
              value={resumeData?.email || ""}
              onChange={(value) => handlePersonalChange("email", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="your.email@example.com"
              error={validationErrors.email}
            />
            <FormInput
              label="GitHub"
              name="github"
              value={resumeData?.github || ""}
              onChange={(value) => handlePersonalChange("github", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="https://github.com/yourusername"
              error={validationErrors.github}
            />
            <FormInput
              label="LinkedIn"
              name="linkedin"
              value={resumeData?.linkedin || ""}
              onChange={(value) => handlePersonalChange("linkedin", value)}
              onFocus={onFieldFocus}
              onBlur={onFieldBlur}
              placeholder="https://linkedin.com/in/yourprofile"
              error={validationErrors.linkedin}
            />
            <div className={`form-group ${validationErrors.hackerrank ? 'error' : ''}`}>
              <label>HackerRank</label>
              <input
                type="text"
                value={resumeData?.hackerrank || ""}
                onChange={(e) => handlePersonalChange("hackerrank", e.target.value)}
                onFocus={() => onFieldFocus("hackerrank")}
                onBlur={() => onFieldBlur("hackerrank")}
                placeholder="https://www.hackerrank.com/yourusername"
              />
              {validationErrors.hackerrank && <p className="error-message">{validationErrors.hackerrank}</p>}
            </div>
            <div className={`form-group ${validationErrors.leetcode ? 'error' : ''}`}>
              <label>LeetCode</label>
              <input
                type="text"
                value={resumeData?.leetcode || ""}
                onChange={(e) => handlePersonalChange("leetcode", e.target.value)}
                onFocus={() => onFieldFocus("leetcode")}
                onBlur={() => onFieldBlur("leetcode")}
                placeholder="https://leetcode.com/yourusername"
              />
              {validationErrors.leetcode && <p className="error-message">{validationErrors.leetcode}</p>}
            </div>
            <WebsiteLinksInput
              websites={resumeData.websites || []}
              newWebsite={newWebsite}
              setNewWebsite={setNewWebsite}
              onAdd={addWebsite}
              onRemove={removeWebsite}
            />
          </div>
        );

      case 'summary':
        return (
          <div className="form-group">
            <label>Professional Summary</label>
            <textarea
              value={resumeData?.professional_summary || ""}
              onChange={(e) => updateField("professional_summary", e.target.value)}
              onFocus={() => onFieldFocus("professional_summary")}
              onBlur={() => onFieldBlur("professional_summary")}
              placeholder="Write a brief summary..."
              rows={5}
            />
          </div>
        );

      case 'highlights':
        return (
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3>Highlights</h3>
              <AddButton onClick={addHighlight}>+ Add</AddButton>
            </div>
            {(resumeData.highlights || []).map((highlight, index) => (
              <div key={index} className="highlight-card">
                <div className="card-header">
                  <h4>Highlight #{index + 1}</h4>
                  <RemoveButton onClick={() => removeHighlight(index)} ariaLabel={`Remove highlight ${index + 1}`}>✕</RemoveButton>
                </div>
                <div className="card-content">
                  <textarea
                    value={highlight}
                    onChange={(e) => updateHighlight(index, e.target.value)}
                    onFocus={() => onFieldFocus(`highlights[${index}]`)}
                    onBlur={() => onFieldBlur(`highlights[${index}]`)}
                    placeholder="e.g., Proven track record..."
                    rows={3}
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case 'experience':
        return (
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3>Work Experience</h3>
              <AddButton onClick={experienceField.addItem}>+ Add</AddButton>
            </div>
            {experienceField.items.map((exp, index) => {
              const errors = validationErrors.work_experience?.[index] || {};
              return (
                <ArrayFieldCard
                  key={index}
                  title={`Experience #${index + 1}`}
                  index={index}
                  onRemove={() => experienceField.removeItem(index)}
                >
                    <FormInput
                      label="Position"
                      name={`work_experience[${index}].position`}
                      value={exp.position || ""}
                      onChange={(value) => experienceField.updateItemField(index, "position", value)}
                      onFocus={onFieldFocus}
                      onBlur={onFieldBlur}
                      placeholder="Your Job Title"
                      error={errors.position}
                    />
                    <FormInput
                      label="Company"
                      name={`work_experience[${index}].company`}
                      value={exp.company || ""}
                      onChange={(value) => experienceField.updateItemField(index, "company", value)}
                      onFocus={onFieldFocus}
                      onBlur={onFieldBlur}
                      placeholder="Company Name"
                      error={errors.company}
                    />
                    <FormInput
                      label="Location"
                      name={`work_experience[${index}].location`}
                      value={exp.location || ""}
                      onChange={(value) => experienceField.updateItemField(index, "location", value)}
                      onFocus={onFieldFocus}
                      onBlur={onFieldBlur}
                      placeholder="City, State"
                    />
                    <FormInput
                      label="Start Date"
                      name={`work_experience[${index}].start_date`}
                      value={exp.start_date || ""}
                      onChange={(value) => experienceField.updateItemField(index, "start_date", value)}
                      onFocus={onFieldFocus}
                      onBlur={onFieldBlur}
                      placeholder="2021-10"
                    />
                    <FormInput
                      label="End Date"
                      name={`work_experience[${index}].end_date`}
                      value={exp.end_date || ""}
                      onChange={(value) => experienceField.updateItemField(index, "end_date", value)}
                      onFocus={onFieldFocus}
                      onBlur={onFieldBlur}
                      placeholder="Present"
                    />
                    <div className="form-group">
                      <label>Duties</label>
                      {exp.duties?.map((duty, dutyIndex) => (
                        <div key={dutyIndex} className="point-input">
                          <textarea
                            value={duty}
                            onChange={(e) => updateDuty(index, dutyIndex, e.target.value)}
                            onFocus={() => onFieldFocus(`work_experience[${index}].duties[${dutyIndex}]`)}
                            onBlur={() => onFieldBlur(`work_experience[${index}].duties[${dutyIndex}]`)}
                            rows={2}
                          />
                          <button onClick={() => removeDuty(index, dutyIndex)} className="remove-point-button">✕</button>
                        </div>
                      ))}
                      <button onClick={() => addDuty(index)} className="add-point-button">+ Add Duty</button>
                    </div>
                </ArrayFieldCard>
              );
            })}
          </div>
        );

      case 'education':
        return (
          <div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3>Education</h3>
              <AddButton onClick={addEducation}>+ Add</AddButton>
            </div>
            {(resumeData.education || []).map((edu, index) => {
              const errors = validationErrors.education?.[index] || {};
              return (
                <div key={index} className="education-card">
                  <div className="card-header">
                    <h4>Education #{index + 1}</h4>
                    <RemoveButton onClick={() => removeEducation(index)} ariaLabel={`Remove education ${index + 1}`}>✕</RemoveButton>
                  </div>
                  <div className="card-content">
                    <div className={`form-group ${errors.degree ? 'error' : ''}`}>
                      <label>Degree / Field of Study</label>
                      <input
                        type="text"
                        value={edu.degree || ""}
                        onChange={(e) => updateEducation(index, "degree", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].degree`)}
                        onBlur={() => onFieldBlur(`education[${index}].degree`)}
                        placeholder="e.g., B.S. in Computer Science"
                      />
                      {errors.degree && <p className="error-message">{errors.degree}</p>}
                    </div>
                    <div className={`form-group ${errors.institute ? 'error' : ''}`}>
                      <label>Institute</label>
                      <input
                        type="text"
                        value={edu.institute || ""}
                        onChange={(e) => updateEducation(index, "institute", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].institute`)}
                        onBlur={() => onFieldBlur(`education[${index}].institute`)}
                        placeholder="University Name"
                      />
                      {errors.institute && <p className="error-message">{errors.institute}</p>}
                    </div>
                    <div className="form-group">
                      <label>Location</label>
                      <input
                        type="text"
                        value={edu.location || ""}
                        onChange={(e) => updateEducation(index, "location", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].location`)}
                        onBlur={() => onFieldBlur(`education[${index}].location`)}
                        placeholder="City, State"
                      />
                    </div>
                    <div className="form-group">
                      <label>Start Year</label>
                      <input
                        type="text"
                        value={edu.start_year || ""}
                        onChange={(e) => updateEducation(index, "start_year", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].start_year`)}
                        onBlur={() => onFieldBlur(`education[${index}].start_year`)}
                        placeholder="2020"
                      />
                    </div>
                    <div className="form-group">
                      <label>End Year</label>
                      <input
                        type="text"
                        value={edu.end_year || ""}
                        onChange={(e) => updateEducation(index, "end_year", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].end_year`)}
                        onBlur={() => onFieldBlur(`education[${index}].end_year`)}
                        placeholder="2024"
                      />
                    </div>
                    <div className="form-group">
                      <label>CGPA</label>
                      <input
                        type="text"
                        value={edu.cgpa || ""}
                        onChange={(e) => updateEducation(index, "cgpa", e.target.value)}
                        onFocus={() => onFieldFocus(`education[${index}].cgpa`)}
                        onBlur={() => onFieldBlur(`education[${index}].cgpa`)}
                        placeholder="7.09/10"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );

      case 'skills':
        return (
          <div className="form-group">
            <h3>Skills</h3>

            <CategorizedSkillsInput
              skills={resumeData.skills || {}}
              onClear={() => {
                const clearedSkills = {
                  programming_languages: [],
                  frameworks: [],
                  tools: [],
                  ai_ml_technologies: [],
                };
                updateField('skills', clearedSkills);
                // Also clear key_skills if they exist
                if (resumeData.key_skills) {
                  onUpdate({ ...resumeData, skills: clearedSkills, key_skills: [] });
                }
              }}
              clearLabel="Clear"
              onChange={(updatedSkills) => {
                updateField('skills', updatedSkills);
                // Keep old behavior: clearing key_skills once structured skills exist
                if (resumeData.key_skills) {
                  onUpdate({ ...resumeData, skills: updatedSkills, key_skills: [] });
                }
              }}
              categories={[
                { key: 'programming_languages', label: 'Programming Languages', placeholder: 'Add a programming language...' },
                { key: 'frameworks', label: 'Frameworks', placeholder: 'Add a framework...' },
                { key: 'tools', label: 'Tools', placeholder: 'Add a tool...' },
                { key: 'ai_ml_technologies', label: 'AI/ML Technologies', placeholder: 'Add an AI/ML technology...' },
              ]}
            />
          </div>
        );

      case 'achievements':
        return (
          <div className="form-group">
            <h3>Achievements</h3>
            <div className="add-skill">
              <input
                type="text"
                placeholder="Add an achievement"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    updateField("achievements", [...(resumeData.achievements || []), e.target.value.trim()]);
                    e.target.value = '';
                  }
                }}
              />
              <button onClick={(e) => {
                const input = e.target.previousSibling;
                if (input.value.trim()) {
                  updateField("achievements", [...(resumeData.achievements || []), input.value.trim()]);
                  input.value = '';
                }
              }}>Add</button>
            </div>
            <div className="skills-list">
              {(resumeData.achievements || []).map((achievement, index) => (
                <span key={index} className="skill-tag">
                  <span>{achievement}</span>
                  <button onClick={() => {
                    updateField("achievements", resumeData.achievements.filter((_, i) => i !== index));
                  }}>×</button>
                </span>
              ))}
            </div>
          </div>
        );

      case 'certifications':
        return (
          <div className="form-group">
            <h3>Certifications</h3>
            <div className="add-skill">
              <input
                type="text"
                placeholder="Add a certification"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    updateField("certifications", [...(resumeData.certifications || []), e.target.value.trim()]);
                    e.target.value = '';
                  }
                }}
              />
              <button onClick={(e) => {
                const input = e.target.previousSibling;
                if (input.value.trim()) {
                  updateField("certifications", [...(resumeData.certifications || []), input.value.trim()]);
                  input.value = '';
                }
              }}>Add</button>
            </div>
            <div className="skills-list">
              {(resumeData.certifications || []).map((cert, index) => (
                <span key={index} className="skill-tag">
                  <span>
                    {typeof cert === 'string' ? cert : (cert.name || cert)}
                    {typeof cert === 'object' && cert.provider ? ` – ${cert.provider}` : ""}
                  </span>
                  <button onClick={() => {
                    updateField("certifications", resumeData.certifications.filter((_, i) => i !== index));
                  }}>×</button>
                </span>
              ))}
            </div>
          </div>
        );

      case 'projects':
        return (
          <div className="form-group">
            <h3>Projects</h3>
            <AddButton onClick={addProject}>+ Add Project</AddButton>
            {(resumeData.projects || []).map((proj, index) => (
              <div key={index} className="experience-card">
                <div className="card-header">
                  <h4>Project {index + 1}</h4>
                  <RemoveButton onClick={() => removeProject(index)} ariaLabel={`Remove project ${index + 1}`} />
                </div>
                <div className="card-content">
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={proj.title || ''}
                      onChange={(e) => updateProject(index, "title", e.target.value)}
                      placeholder="Project Title"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={proj.description || ''}
                      onChange={(e) => updateProject(index, "description", e.target.value)}
                      placeholder="A short description of your project."
                      rows={3}
                    />
                  </div>
                  <div className="form-group">
                    <label>Link</label>
                    <input
                      type="text"
                      value={proj.github_link || proj.link || ''}
                      onChange={(e) => updateProject(index, "github_link", e.target.value)}
                      placeholder="https://github.com/your/project"
                    />
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <input
                      type="text"
                      value={proj.year || ""}
                      onChange={(e) => updateProject(index, "year", e.target.value)}
                      placeholder="2025"
                    />
                  </div>
                  <div className="form-group">
                    <label>Technologies (comma separated)</label>
                    <input
                      type="text"
                      value={Array.isArray(proj.tech_stack) ? proj.tech_stack.join(', ') : ''}
                      onChange={(e) => {
                        const updated = [...(resumeData.projects || [])];
                        updated[index] = { ...proj, tech_stack: e.target.value.split(',').map(t => t.trim()) };
                        updateField("projects", updated);
                      }}
                      placeholder="React, Node.js, Firebase"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case 'additional':
        return (
          <div className="form-group">
            <label>References Note</label>
            <textarea
              value={resumeData?.references_note || ""}
              onChange={(e) => updateField("references_note", e.target.value)}
              onFocus={() => onFieldFocus("references_note")}
              onBlur={() => onFieldBlur("references_note")}
              placeholder="e.g., References available upon request."
              rows={2}
            />
          </div>
        );

      default:
        // Render dynamic sections from ResumeEditor
        if (renderDynamicSectionForm && getDynamicSections) {
          const dynamicSection = getDynamicSections().find(s => s.id === activeSection);
          if (dynamicSection) {
            return renderDynamicSectionForm(dynamicSection.id);
          }
        }
        return null;
    }
  };

  return (
    <div className="resume-form">
      <div className="form-header">
        <h2 className="form-title">Resume Editor</h2>
      </div>
      <SectionSelector
        sections={sections}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
      />
      <div className="section-form">
        {renderSection()}
      </div>
    </div>
  );
};

export default SimpleResumeForm;
